import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays,
  Images,
  Loader2,
  CheckCircle2,
  Plus,
  ArrowRight,
  Upload,
  Sparkles,
  TrendingUp,
} from 'lucide-react';

import { useAuthStore } from '@/store/authStore';
import { useEvents } from '@/api/hooks/useEvents';
import { EventCard } from '@/components/events/EventCard';
import type { EventListItem } from '@/api/types/event.types';

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const { data: eventsData, isLoading, isError } = useEvents({ page: 1 });

  const events = eventsData?.results ?? [];
  const totalEvents = eventsData?.count ?? 0;

  const stats = useMemo(() => {
    const totalPhotos = events.reduce((sum: number, e: EventListItem) => sum + e.photo_count, 0);
    const processingCount = events.filter(
      (e: EventListItem) => e.processing_status === 'processing' || e.processing_status === 'uploading',
    ).length;
    const completedCount = events.filter(
      (e: EventListItem) => e.processing_status === 'completed',
    ).length;
    return { totalPhotos, processingCount, completedCount };
  }, [events]);

  const recentEvents = events.slice(0, 5);
  const hasEvents = totalEvents > 0;

  const greeting = user?.first_name
    ? t('dashboard.welcome', { name: user.first_name })
    : t('dashboard.welcomeGeneric');

  // ---- Empty / onboarding state ----
  if (!isLoading && !isError && !hasEvents) {
    return (
      <div className="page-container">
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-primary-50 ring-1 ring-primary-100 dark:bg-primary-500/10 dark:ring-primary-500/20">
            <Sparkles className="h-9 w-9 text-primary-500 dark:text-primary-400" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.emptyTitle')}
          </h1>
          <p className="mt-3 max-w-md text-sm text-gray-500 dark:text-gray-500">
            {t('dashboard.emptyDescription')}
          </p>
          <Link to="/events/create" className="btn-primary mt-8">
            <Plus className="h-4 w-4" />
            {t('dashboard.getStarted')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title">{greeting}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            {t('dashboard.overview')}
          </p>
        </div>
        <Link to="/events/create" className="btn-primary mt-4 sm:mt-0">
          <Plus className="h-4 w-4" />
          {t('dashboard.createEvent')}
        </Link>
      </div>

      {/* Stats row */}
      <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('dashboard.totalEvents')}
          value={isLoading ? '--' : String(totalEvents)}
          icon={<CalendarDays className="h-5 w-5" />}
          accent="orange"
        />
        <StatCard
          label={t('dashboard.totalPhotos')}
          value={isLoading ? '--' : String(stats.totalPhotos)}
          icon={<Images className="h-5 w-5" />}
          accent="blue"
        />
        <StatCard
          label={t('dashboard.processing')}
          value={isLoading ? '--' : String(stats.processingCount)}
          icon={<Loader2 className={`h-5 w-5 ${stats.processingCount > 0 ? 'animate-spin' : ''}`} />}
          accent="amber"
        />
        <StatCard
          label={t('dashboard.completedEvents')}
          value={isLoading ? '--' : String(stats.completedCount)}
          icon={<CheckCircle2 className="h-5 w-5" />}
          accent="green"
        />
      </div>

      {/* Main grid */}
      <div className="mt-7 grid gap-6 lg:grid-cols-3">
        {/* Recent Events */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
              {t('dashboard.recentEvents')}
            </h2>
            <Link
              to="/events"
              className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors dark:text-primary-400 dark:hover:text-primary-300"
            >
              {t('dashboard.viewAllEvents')}
              <ArrowRight className="icon-directional h-3.5 w-3.5" />
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-xl border border-gray-200 bg-gray-100 dark:border-surface-border dark:bg-surface-card"
                />
              ))}
            </div>
          ) : isError ? (
            <div className="card text-center text-sm text-gray-500">
              {t('events.loadError')}
            </div>
          ) : recentEvents.length === 0 ? (
            <div className="card text-center text-sm text-gray-500">
              {t('dashboard.noRecentEvents')}
            </div>
          ) : (
            <div className="space-y-2">
              {recentEvents.map((event: EventListItem) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500">
            {t('dashboard.quickActions')}
          </h2>
          <div className="space-y-2">
            <QuickAction
              to="/events/create"
              icon={<Plus className="h-4 w-4" />}
              iconColor="text-primary-400"
              iconBg="bg-primary-500/10"
              title={t('dashboard.createEvent')}
              description={t('events.emptyDescription')}
            />
            <QuickAction
              to="/upload"
              icon={<Upload className="h-4 w-4" />}
              iconColor="text-blue-400"
              iconBg="bg-blue-500/10"
              title={t('dashboard.uploadPhotos')}
              description={t('upload.dragPrompt')}
            />
            <QuickAction
              to="/events"
              icon={<TrendingUp className="h-4 w-4" />}
              iconColor="text-green-400"
              iconBg="bg-green-500/10"
              title={t('dashboard.viewAllEvents')}
              description={t('events.searchPlaceholder')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── StatCard ── */

const accentMap: Record<string, { icon: string; value: string; bar: string }> = {
  orange: {
    icon: 'text-primary-400 bg-primary-500/10',
    value: 'text-white',
    bar: 'bg-primary-500',
  },
  blue: {
    icon: 'text-blue-400 bg-blue-500/10',
    value: 'text-white',
    bar: 'bg-blue-500',
  },
  amber: {
    icon: 'text-amber-400 bg-amber-500/10',
    value: 'text-white',
    bar: 'bg-amber-500',
  },
  green: {
    icon: 'text-green-400 bg-green-500/10',
    value: 'text-white',
    bar: 'bg-green-500',
  },
};

function StatCard({
  label,
  value,
  icon,
  accent = 'orange',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent?: string;
}) {
  const colors = accentMap[accent] ?? accentMap.orange;

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-surface-border dark:bg-surface-card dark:shadow-none">
      {/* Accent bar top */}
      <div className={`absolute inset-x-0 top-0 h-0.5 ${colors.bar} opacity-60`} />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${colors.icon}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

/* ── QuickAction ── */

function QuickAction({
  to,
  icon,
  iconColor,
  iconBg,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:border-surface-border dark:bg-surface-card dark:shadow-none dark:hover:border-surface-hover dark:hover:bg-surface-elevated"
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-800 transition-colors group-hover:text-gray-900 dark:text-gray-200 dark:group-hover:text-white">
          {title}
        </p>
        <p className="truncate text-xs text-gray-500 mt-0.5 dark:text-gray-600">{description}</p>
      </div>
      <ArrowRight className="icon-directional ms-auto h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:text-gray-700 dark:group-hover:text-gray-400" />
    </Link>
  );
}
